import React from 'react'
import { Clock, AlertTriangle, CheckCircle2 } from 'lucide-react'

type NilGoStatus = 'not_started' | 'in_progress' | 'confirmed' | 'not_required'
type InstitutionalDisclosureStatus = 'not_started' | 'submitted' | 'acknowledged' | 'not_required'

interface Props {
  dealExecutedAt: string
  paymentAmount: number
  nilGoStatus: NilGoStatus
  institutionalStatus: InstitutionalDisclosureStatus
  school: { name: string } | null
}

function addBusinessDays(startDate: Date, days: number): Date {
  const result = new Date(startDate)
  let added = 0
  while (added < days) {
    result.setDate(result.getDate() + 1)
    const dayOfWeek = result.getDay()
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      added++
    }
  }
  return result
}

function getDaysRemaining(deadline: Date): number {
  const today = new Date()
  const diff = deadline.getTime() - today.getTime()
  return Math.ceil(diff / (1000 * 60 * 60 * 24))
}

/** How urgent a deadline is: plenty of time, act soon, or overdue */
type Urgency = 'ok' | 'soon' | 'overdue'
const urgencyOf = (days: number): Urgency => (days > 3 ? 'ok' : days >= 1 ? 'soon' : 'overdue')
const URGENCY: Record<Urgency, { text: string; chip: string }> = {
  ok: { text: 'text-foreground', chip: 'bg-[#E8F9D2] text-[#2F5E06]' },
  soon: { text: 'text-[#92400E]', chip: 'bg-[#FEF3C7] text-[#92400E]' },
  overdue: { text: 'text-[#B91C1C]', chip: 'bg-[#FEE2E2] text-[#B91C1C]' },
}

function getCountdownText(daysRemaining: number): string {
  if (daysRemaining > 3) return `${daysRemaining} days left`
  if (daysRemaining === 1) return '1 day left'
  if (daysRemaining > 0) return `${daysRemaining} days left`
  return 'Overdue'
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const Badge: React.FC<{ tone: 'done' | 'progress' | 'none'; children: React.ReactNode }> = ({
  tone,
  children,
}) => (
  <span
    className={
      'inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ' +
      (tone === 'done'
        ? 'bg-[#E8F9D2] text-[#2F5E06]'
        : tone === 'progress'
          ? 'bg-[#FEF3C7] text-[#92400E]'
          : 'bg-muted text-muted-foreground')
    }
  >
    {tone === 'done' ? <CheckCircle2 className="h-3.5 w-3.5" /> : null}
    {children}
  </span>
)

function NilGoStatusBadge({ status }: { status: NilGoStatus }) {
  if (status === 'confirmed') return <Badge tone="done">Confirmed</Badge>
  if (status === 'in_progress') return <Badge tone="progress">In progress</Badge>
  if (status === 'not_required') return <Badge tone="none">Under $600</Badge>
  return <Badge tone="none">Not submitted</Badge>
}

function InstitutionalStatusBadge({ status }: { status: InstitutionalDisclosureStatus }) {
  if (status === 'acknowledged') return <Badge tone="done">Acknowledged</Badge>
  if (status === 'submitted') return <Badge tone="done">Submitted</Badge>
  if (status === 'not_required') return <Badge tone="none">Under $600</Badge>
  return <Badge tone="none">Not started</Badge>
}

/** One filing: what it is, its status, how long is left and the rule behind it */
const DeadlineCard: React.FC<{
  title: string
  badge: React.ReactNode
  days: number
  due: Date
  done: boolean
  notRequired: string | null
  note: string | null
}> = ({ title, badge, days, due, done, notRequired, note }) => {
  const u = URGENCY[urgencyOf(days)]
  return (
    <div className="space-y-3 rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <p className="flex min-w-0 items-start gap-2 text-sm font-semibold text-foreground">
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          {title}
        </p>
        {badge}
      </div>
      {notRequired ? (
        <p className="text-sm text-muted-foreground">{notRequired}</p>
      ) : done ? (
        <p className="text-sm text-muted-foreground">Filed. Nothing more to do here.</p>
      ) : (
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <p className={`text-lg font-bold ${u.text}`}>{getCountdownText(days)}</p>
          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${u.chip}`}>
            Due {formatDate(due)}
          </span>
        </div>
      )}
      {note && !done && !notRequired ? (
        <p className="text-xs text-muted-foreground">{note}</p>
      ) : null}
    </div>
  )
}

const ComplianceDeadlineBanner = ({
  dealExecutedAt,
  nilGoStatus,
  institutionalStatus,
  school,
}: Props) => {
  const executedDate = new Date(dealExecutedAt)
  const nilGoDeadline = addBusinessDays(executedDate, 5)
  const institutionalDeadline = new Date(executedDate)
  institutionalDeadline.setDate(institutionalDeadline.getDate() + 30)

  const nilGoDays = getDaysRemaining(nilGoDeadline)
  const institutionalDays = getDaysRemaining(institutionalDeadline)

  const schoolName = school?.name ?? 'Your School'

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <DeadlineCard
          title="CSC / NIL Go reporting"
          badge={<NilGoStatusBadge status={nilGoStatus} />}
          days={nilGoDays}
          due={nilGoDeadline}
          done={nilGoStatus === 'confirmed'}
          notRequired={
            nilGoStatus === 'not_required' ? 'No NIL Go submission needed for this deal.' : null
          }
          note="Late reporting means ineligibility until it's filed."
        />
        <DeadlineCard
          title={`${schoolName} disclosure`}
          badge={<InstitutionalStatusBadge status={institutionalStatus} />}
          days={institutionalDays}
          due={institutionalDeadline}
          done={institutionalStatus === 'submitted' || institutionalStatus === 'acknowledged'}
          notRequired={
            institutionalStatus === 'not_required'
              ? 'No school disclosure needed for this deal.'
              : null
          }
          note="Include the deal terms, compensation and service provider."
        />
      </div>

      <div className="flex items-start gap-2.5 rounded-xl border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#B45309]" />
        <p className="text-xs leading-relaxed text-[#78350F]">
          <span className="font-semibold">NCAA rules are your responsibility.</span> Athios gives
          you this as a guide, not legal advice. Deals of $600+ must be reported to NIL Go within 5
          business days of signing. Ask your athletic department's compliance officer if you're
          unsure.
        </p>
      </div>
    </div>
  )
}

export default ComplianceDeadlineBanner
